import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth.jsx';
import { useProfile } from '../lib/profile.jsx';
import { useIsMobile } from '../lib/useIsMobile.js';

// ─── Exam companion — the training checklist that follows you into the sandbox ─
// A trainee's exam lives in Admin → Training, but the WORK happens inside the
// sandbox client's experience. This floats a small pill (bottom-left, clear of
// the Owl FAB bottom-right) whenever an admin with an in-progress attempt is
// acting as that exam's sandbox client. Opening it shows the exam code, the
// live task checklist and a "Check my work" — so trainees grade as they go
// without bouncing back to the console. Renders nothing for everyone else.

async function j(url, opts) {
  const r = await fetch(url, opts ? { headers: { 'Content-Type': 'application/json' }, ...opts, body: opts.body ? JSON.stringify(opts.body) : undefined } : undefined);
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);
  return data;
}

export default function ExamCompanion() {
  const { isAdmin } = useAuth();
  const { mode, activeEntityId } = useProfile();
  const isMobile = useIsMobile();
  const [attempt, setAttempt] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const inClient = isAdmin && mode === 'client' && !!activeEntityId;
  useEffect(() => {
    if (!inClient) { setAttempt(null); return; }
    let dead = false;
    j('/api/training/my')
      .then((d) => { if (!dead) setAttempt((d.attempts || []).find((a) => a.status === 'in_progress' && a.exam.entityId === activeEntityId) || null); })
      .catch(() => {});
    return () => { dead = true; };
  }, [inClient, activeEntityId]);

  if (!attempt) return null;

  const check = async () => {
    setBusy(true); setErr('');
    try {
      await j(`/api/training/my/${attempt.id}/check`, { method: 'POST', body: {} });
      const d = await j('/api/training/my');
      setAttempt((d.attempts || []).find((a) => a.id === attempt.id) || null);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const doneCount = attempt.tasks.filter((t) => t.done).length;
  const pill = { position: 'fixed', bottom: 20, left: isMobile ? 16 : 24, zIndex: 55 };
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        title="Your exam checklist — tasks are checked inside this sandbox client"
        style={{ ...pill, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 999, border: '1.5px dashed var(--brand, #6d5df6)', background: 'var(--card, #fff)', color: 'var(--text)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', boxShadow: '0 6px 22px rgba(0,0,0,0.25)' }}
      >
        ✎ Exam {attempt.code} · {doneCount}/{attempt.tasks.length}
      </button>
    );
  }
  return (
    <div style={{ ...pill, width: isMobile ? 'calc(100vw - 32px)' : 360, maxHeight: '70dvh', display: 'flex', flexDirection: 'column', borderRadius: 14, border: '1px solid var(--hairline)', background: 'var(--card, #fff)', color: 'var(--text)', boxShadow: '0 10px 34px rgba(0,0,0,0.3)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--hairline)' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{attempt.exam.title}</div>
          <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>Code <strong style={{ userSelect: 'all' }}>{attempt.code}</strong> — name everything you create with it</div>
        </div>
        <button onClick={() => setOpen(false)} aria-label="Minimise" style={{ border: 0, background: 'transparent', color: 'var(--muted)', fontSize: 16, cursor: 'pointer', padding: 4 }}>✕</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '4px 14px' }}>
        {attempt.tasks.map((t) => (
          <div key={t.key} style={{ display: 'flex', gap: 9, padding: '8px 0', borderTop: '1px solid var(--hairline)', alignItems: 'flex-start' }}>
            <span style={{ fontSize: 15, lineHeight: '20px' }}>{t.done ? '✓' : t.emoji}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 12.5, color: t.done ? 'var(--muted)' : 'var(--text)' }}>{t.title}</div>
              {!t.done && <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1, lineHeight: 1.4 }}>{t.brief}</div>}
              {!t.done && t.evidence && <div style={{ fontSize: 11.5, marginTop: 2, color: '#c2410c' }}>⚠ {t.evidence}</div>}
            </div>
          </div>
        ))}
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: 12, padding: '0 14px 6px' }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderTop: '1px solid var(--hairline)', alignItems: 'center' }}>
        <button onClick={check} disabled={busy} style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: 0, background: 'var(--text)', color: 'var(--bg, #fff)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
          {busy ? 'Checking…' : '⟳ Check my work'}
        </button>
        <span style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>{doneCount}/{attempt.tasks.length} done</span>
      </div>
    </div>
  );
}
